import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackContext, useSafeNavigation, useSafeRouter } from '@sg/garnish';

import { type OrderingContext } from '@order/features/ordering';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { useNavigateToReorderWithinModalStack } from './useNavigateToReorderWithinModalStack';

/**
 * Hook to change the location or channel if logged in and navigate to auth otherwise.
 */
export const useChangeChannelOrLocation = () => {
  const isLoggedIn = useIsLoggedIn();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const startReorder = useNavigateToReorderWithinModalStack();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const changeChannelOrLocation = useCallback(
    ({ context }: { context: OrderingContext }) => {
      if (!isLoggedIn) {
        const text = formatMessage(messages.reorderLoggedOut);

        addNoticeBanner({ text, palette: 'neutral' });

        if (isExpoRouterEnabled) {
          router?.dismissTo({
            pathname: '/(auth)/join-or-sign-in',
            params: { redirect: 'locations' },
          });
          return;
        }

        navigation?.navigate('Auth', {
          screen: 'Login',
          params: { redirect: 'locations' },
        });

        return;
      }

      const cart = context?.cart;

      if (!cart) {
        const text = formatMessage(messages.reorderNoCart);
        addNoticeBanner({ text, palette: 'caution' });
        return;
      }

      startReorder({
        orderId: cart.orderId,
        orderChannel: cart.orderChannel,
        restaurantId: cart.restaurantId,
        locationName: cart.locationName,
        restaurantSlug: cart.restaurantSlug,
        addressId: cart.addressId,
        deliveryOrderDetail: cart.deliveryOrderDetail,
      });
    },
    [
      isLoggedIn,
      isExpoRouterEnabled,
      navigation,
      router,
      startReorder,
      formatMessage,
      addNoticeBanner,
    ],
  );

  return { changeChannelOrLocation };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  reorderLoggedOut: {
    defaultMessage: 'Please sign in to update your order location.',
    description: 'Bag | Header | Reorder Logged Out',
  },
  reorderNoCart: {
    defaultMessage: 'Something went wrong. Please try again.',
    description: 'Bag | Header | Reorder No Cart',
  },
});
